import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Copy, KeyRound, Smartphone, Trash2 } from 'lucide-react';
import { cpApi, type CpSmsAttachCandidate, type CpSmsPhone, type CpSmsPhoneProfile } from '../api/api';
import VndPageLoading from '../components/VndPageLoading';
import DeleteConfirmationWidget from '../components/DeleteConfirmationWidget';
import { CustomSelect } from '../components/ui/CustomSelect';

function apiMessage(e: unknown, fallback: string) {
  if (axios.isAxiosError(e)) {
    const d = e.response?.data as { error?: string; message?: string } | string | undefined;
    const fromApi = typeof d === 'string' ? d.trim() : (d?.message || d?.error || '').trim();
    return fromApi || fallback;
  }
  return e instanceof Error ? e.message : fallback;
}

function confirmChannel(item: { listen_on?: boolean; payment_provider?: string; work_mode?: string }) {
  if (item.listen_on) return 'sms';
  if (item.work_mode && item.work_mode !== 'auto') return 'none';
  return item.payment_provider === 'casso' ? 'casso' : 'sepay';
}

function channelLabel(channel: 'sms' | 'casso' | 'sepay' | 'none', manualLabel: string) {
  if (channel === 'sms') return 'SMS 🟡';
  if (channel === 'casso') return 'Casso 🟢';
  if (channel === 'sepay') return 'SePay 🔵';
  return manualLabel;
}

function phoneBound(p: CpSmsPhone) {
  return Boolean(p.last_seen_at || p.device_label);
}

function phoneSetup(p: CpSmsPhone) {
  const hasProfile = (p.profiles ?? []).length > 0;
  const bound = phoneBound(p);
  const online = p.connection === 'online' && !p.idle_paused;
  const current: 1 | 2 | 3 = !hasProfile ? 1 : !bound ? 2 : 3;
  return { hasProfile, bound, online, current };
}

function SetupStepper({
  current,
  bound,
  online,
  labels,
}: {
  current: 1 | 2 | 3;
  bound: boolean;
  online: boolean;
  labels: [string, string, string];
}) {
  const items = [
    { n: 1 as const, label: labels[0], done: current > 1 },
    { n: 2 as const, label: labels[1], done: bound },
    { n: 3 as const, label: labels[2], done: online },
  ];
  return (
    <ol className="mb-1 grid grid-cols-3 gap-1">
      {items.map((it) => {
        const active = current === it.n && !it.done;
        return (
          <li
            key={it.n}
            className={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-semibold ${
              it.done
                ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200'
                : active
                  ? 'bg-sky-100 text-sky-900 ring-1 ring-sky-300 dark:bg-sky-500/20 dark:text-sky-100'
                  : 'bg-slate-50 text-slate-400 dark:bg-white/[0.04]'
            }`}
          >
            <span
              className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] ${
                it.done ? 'bg-emerald-500 text-white' : active ? 'bg-sky-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}
            >
              {it.done ? '✓' : it.n}
            </span>
            {it.n}. {it.label}
          </li>
        );
      })}
    </ol>
  );
}

function stepBoxClass(opts: { locked?: boolean; active?: boolean; done?: boolean }) {
  if (opts.locked) {
    return 'mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/90 p-3 dark:border-white/10 dark:bg-white/[0.03]';
  }
  if (opts.active) return 'mt-3 rounded-xl border-2 border-sky-400 bg-sky-50/40 p-3 dark:border-sky-400 dark:bg-sky-500/10';
  if (opts.done) return 'mt-3 rounded-xl border border-emerald-200 bg-white p-3 dark:border-emerald-500/30 dark:bg-white/[0.04]';
  return 'mt-3 rounded-xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-white/[0.04]';
}

export default function VndSmsPhonesPage() {
  const { t } = useTranslation();
  const [phones, setPhones] = useState<CpSmsPhone[]>([]);
  const [candidates, setCandidates] = useState<CpSmsAttachCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shownKeys, setShownKeys] = useState<Record<string, string>>({});
  const [attachFor, setAttachFor] = useState<Record<string, string>>({});
  const [deletePhone, setDeletePhone] = useState<CpSmsPhone | null>(null);
  const [rotateId, setRotateId] = useState<string | null>(null);
  const [detachTarget, setDetachTarget] = useState<{ phone: CpSmsPhone; profile: CpSmsPhoneProfile } | null>(null);

  const load = useCallback(async () => {
    const data = await cpApi.listSmsPhones();
    setPhones(data.phones);
    setCandidates(data.candidates);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await load();
        if (!cancelled) setError(null);
      } catch (e) {
        if (!cancelled) setError(apiMessage(e, t('smsPhones.loadError')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    const timer = window.setInterval(() => {
      void load().catch(() => undefined);
    }, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [load, t]);

  const candOptions = useMemo(
    () =>
      candidates.map((c) => ({
        value: String(c.id),
        label: `${c.name || `#${c.id}`}${c.bank_name ? ` · ${c.bank_name}` : ''}${c.account_no ? ` ${c.account_no}` : ''} · ${channelLabel(confirmChannel(c), t('qrProfiles.modeManual'))}`,
      })),
    [candidates, t],
  );

  const wrap = useCallback(async (fn: () => Promise<CpSmsPhone | void>) => {
    setBusy(true);
    window.dispatchEvent(new CustomEvent('vndSmsPhonesBusy', { detail: { busy: true } }));
    try {
      const phone = await fn();
      if (phone?.bind_key) {
        setShownKeys((prev) => ({ ...prev, [phone.id]: phone.bind_key as string }));
      }
      await load();
      setError(null);
    } catch (e) {
      const code = axios.isAxiosError(e)
        ? String((e.response?.data as { error?: string } | undefined)?.error || '')
        : '';
      if (code === 'phone_has_foreign_profiles') {
        setError(t('smsPhones.foreignProfiles'));
      } else {
        setError(apiMessage(e, t('smsPhones.saveError')));
      }
    } finally {
      setBusy(false);
      window.dispatchEvent(new CustomEvent('vndSmsPhonesBusy', { detail: { busy: false } }));
    }
  }, [load, t]);

  useEffect(() => {
    const onCreate = () => {
      void wrap(async () => {
        const phone = await cpApi.createSmsPhone({});
        window.dispatchEvent(
          new CustomEvent('showToast', { detail: { message: t('smsPhones.createdToast'), type: 'success' } }),
        );
        return phone;
      });
    };
    window.addEventListener('vndSmsPhonesCreate', onCreate);
    return () => window.removeEventListener('vndSmsPhonesCreate', onCreate);
  }, [t, wrap]);

  const connLabel = (p: CpSmsPhone) => {
    if (!p.enabled) return t('smsPhones.connOff');
    if (p.idle_paused) return t('smsPhones.connIdle');
    if (p.connection === 'online') return t('smsPhones.connOnline');
    if (p.connection === 'offline') return t('smsPhones.connOffline');
    return t('smsPhones.connWaiting');
  };

  if (loading) return <VndPageLoading />;

  return (
    <div className="px-3 py-4 sm:px-5">
      {error ? <p className="mb-3 text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}

      {phones.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-500 dark:border-white/15 dark:bg-white/[0.04] dark:text-slate-400">
          {t('smsPhones.empty')}
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {phones.map((p) => {
            const key = shownKeys[p.id] || p.bind_key;
            const selected = attachFor[p.id] || candOptions[0]?.value || '';
            const managed = p.managed !== false;
            const setup = phoneSetup(p);
            return (
              <section
                key={p.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.04]"
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-500 text-white">
                      <Smartphone className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      {managed ? (
                        <input
                          defaultValue={p.label || ''}
                          key={`${p.id}-${p.label}`}
                          disabled={busy}
                          onBlur={(e) => {
                            const next = e.target.value.trim();
                            if (next === (p.label || '')) return;
                            void wrap(() => cpApi.putSmsPhone(p.id, { label: next }));
                          }}
                          className="w-full bg-transparent text-sm font-semibold text-slate-900 outline-none dark:text-white"
                          placeholder={t('smsPhones.unnamed')}
                        />
                      ) : (
                        <div className="text-sm font-semibold text-slate-900 dark:text-white">
                          {p.label || t('smsPhones.unnamed')}
                        </div>
                      )}
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {phoneBound(p) ? connLabel(p) : t('smsPhones.notConnected')}
                        {!managed ? ` · ${t('smsPhones.adminBound')}` : ''}
                      </p>
                    </div>
                  </div>
                  {managed ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setDeletePhone(p)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-rose-200 hover:text-rose-600 dark:border-white/15 dark:bg-white/5"
                      title={t('smsPhones.delete')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>

                <SetupStepper
                  current={setup.current}
                  bound={setup.bound}
                  online={setup.online}
                  labels={[t('smsPhones.stepProfile'), t('smsPhones.stepKey'), t('smsPhones.stepLink')]}
                />

                <div className={stepBoxClass({ active: setup.current === 1, done: setup.hasProfile })}>
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('smsPhones.step1Title')}
                  </div>
                  {(p.profiles ?? []).length === 0 ? (
                    <p className="mb-2 text-[12px] text-slate-500 dark:text-slate-400">{t('smsPhones.stepAttachFirst')}</p>
                  ) : (
                    <ul className="mb-2 flex flex-wrap gap-2">
                      {(p.profiles ?? []).map((pr) => (
                        <li
                          key={`${pr.source}-${pr.id}`}
                          className="inline-flex min-h-10 max-w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 py-1 pl-2.5 pr-1.5 dark:border-white/10 dark:bg-white/[0.04]"
                        >
                          <span className="min-w-0 truncate text-[12px] font-medium text-slate-800 dark:text-slate-100">
                            {pr.name || `#${pr.id}`}
                            <span className="ml-1 font-normal text-slate-500">
                              {pr.bank_name} {pr.account_no}
                            </span>
                          </span>
                          <span className="shrink-0 rounded-md bg-white px-1.5 py-0.5 text-[11px] font-semibold text-slate-700 dark:bg-white/10 dark:text-slate-200">
                            {channelLabel(confirmChannel(pr), t('qrProfiles.modeManual'))}
                          </span>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              void wrap(async () => {
                                await cpApi.putVndQrProfileSms(pr.id, { enabled: !pr.listen_on });
                              })
                            }
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full ${
                              pr.listen_on ? 'bg-violet-500' : 'bg-slate-300 dark:bg-slate-600'
                            } disabled:opacity-50`}
                            title={pr.listen_on ? t('smsPhones.listenOn') : t('smsPhones.listenOff')}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ${
                                pr.listen_on ? 'translate-x-5' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setDetachTarget({ phone: p, profile: pr })}
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-rose-200 hover:text-rose-600 dark:border-white/15 dark:bg-white/5"
                            title={t('smsPhones.detach')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {candOptions.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <CustomSelect
                        variant="surface"
                        className="min-w-0 flex-1"
                        value={selected}
                        options={candOptions}
                        onChange={(v) => setAttachFor((prev) => ({ ...prev, [p.id]: v }))}
                        disabled={busy}
                      />
                      <button
                        type="button"
                        disabled={busy || !selected}
                        onClick={() => {
                          const profileId = Number(selected);
                          if (!profileId) return;
                          void wrap(() => cpApi.attachSmsPhoneProfile(p.id, profileId));
                        }}
                        className="h-9 shrink-0 rounded-lg border border-sky-200 bg-sky-50 px-3 text-[12px] font-semibold text-sky-800 disabled:opacity-50 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200"
                      >
                        {t('smsPhones.attach')}
                      </button>
                    </div>
                  ) : (
                    <p className="text-[12px] text-slate-400">{t('smsPhones.noFreeProfiles')}</p>
                  )}
                </div>

                <div
                  className={stepBoxClass({
                    locked: !setup.hasProfile,
                    active: setup.current === 2,
                    done: setup.bound,
                  })}
                >
                  <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-200">
                    <KeyRound className="h-3.5 w-3.5" />
                    {t('smsPhones.step2Title')}
                  </div>
                  {!setup.hasProfile ? (
                    <p className="text-[12px] text-slate-500 dark:text-slate-400">{t('smsPhones.stepKeyLocked')}</p>
                  ) : !managed ? (
                    <p className="text-[12px] text-slate-600 dark:text-slate-300">{t('smsPhones.keyAdminOnly')}</p>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-emerald-200/90 bg-gradient-to-br from-emerald-50 via-white to-sky-50 dark:border-emerald-500/25 dark:from-emerald-500/10 dark:via-white/[0.03] dark:to-transparent">
                      <div className="flex items-center justify-between gap-2 border-b border-emerald-100 px-3 py-2 dark:border-emerald-500/20">
                        <p className="text-[12px] text-emerald-900 dark:text-emerald-100">
                          {setup.bound ? t('smsPhones.stepKeyInApp') : t('smsPhones.stepPasteKey')}
                        </p>
                        {key ? (
                          <button
                            type="button"
                            className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white"
                            onClick={() => {
                              void navigator.clipboard.writeText(key);
                              window.dispatchEvent(
                                new CustomEvent('showToast', { detail: { message: t('smsPhones.keyCopied'), type: 'success' } }),
                              );
                            }}
                          >
                            <Copy className="h-3 w-3" />
                            {t('smsPhones.copyKey')}
                          </button>
                        ) : null}
                      </div>
                      {key ? (
                        <code className="block break-all px-3 py-2.5 font-mono text-[12px] text-slate-900 dark:text-slate-100">{key}</code>
                      ) : (
                        <div className="px-3 py-2.5">
                          <p className="text-[12px] text-slate-600 dark:text-slate-300">{t('smsPhones.stepIssueHint')}</p>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setRotateId(p.id)}
                            className="mt-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-50"
                          >
                            {t('smsPhones.issueKey')}
                          </button>
                        </div>
                      )}
                      {key ? (
                        <div className="border-t border-emerald-100 px-3 py-2 dark:border-emerald-500/20">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setRotateId(p.id)}
                            className="text-[12px] font-semibold text-rose-700 disabled:opacity-50 dark:text-rose-300"
                          >
                            {t('smsPhones.rotateKey')}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>

                <div
                  className={stepBoxClass({
                    locked: !setup.hasProfile,
                    active: setup.current === 3,
                    done: setup.online,
                  })}
                >
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('smsPhones.step3Title')}
                  </div>
                  {!setup.hasProfile ? (
                    <p className="text-[12px] text-slate-500 dark:text-slate-400">{t('smsPhones.stepLinkLocked')}</p>
                  ) : managed ? (
                    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 dark:border-white/10 dark:bg-white/[0.04]">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{t('smsPhones.inWork')}</div>
                      </div>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void wrap(() => cpApi.putSmsPhone(p.id, { enabled: !p.enabled }))}
                        className={`relative inline-flex h-7 w-11 shrink-0 items-center rounded-full ${
                          p.enabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
                        } disabled:opacity-50`}
                        aria-pressed={!!p.enabled}
                        title={t('smsPhones.inWorkHint')}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ${
                            p.enabled ? 'translate-x-[22px]' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  ) : (
                    <p className="text-[12px] text-slate-500 dark:text-slate-400">{connLabel(p)}</p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <DeleteConfirmationWidget
        isOpen={Boolean(deletePhone)}
        onClose={() => setDeletePhone(null)}
        onConfirm={() => {
          if (!deletePhone) return;
          const id = deletePhone.id;
          void wrap(async () => {
            await cpApi.deleteSmsPhone(id);
            setDeletePhone(null);
            setShownKeys((prev) => {
              const next = { ...prev };
              delete next[id];
              return next;
            });
            window.dispatchEvent(
              new CustomEvent('showToast', { detail: { message: t('smsPhones.deletedToast'), type: 'success' } }),
            );
          });
        }}
        title={t('smsPhones.deleteTitle')}
        message={t('smsPhones.deleteMsg')}
        itemName={deletePhone ? deletePhone.label || deletePhone.device_label || deletePhone.id : undefined}
        confirmText={t('common.delete')}
        warningText={t('smsPhones.deleteWarn')}
        isConfirmLoading={busy}
      />
      <DeleteConfirmationWidget
        isOpen={Boolean(rotateId)}
        onClose={() => setRotateId(null)}
        onConfirm={() => {
          if (!rotateId) return;
          const id = rotateId;
          void wrap(async () => {
            const next = await cpApi.rotateSmsPhoneKey(id);
            setRotateId(null);
            window.dispatchEvent(
              new CustomEvent('showToast', { detail: { message: t('smsPhones.rotatedToast'), type: 'success' } }),
            );
            return next;
          });
        }}
        title={t('smsPhones.rotateTitle')}
        message={t('smsPhones.rotateMsg')}
        confirmText={t('smsPhones.rotateConfirm')}
        warningText={t('smsPhones.rotateWarn')}
        isConfirmLoading={busy}
      />
      <DeleteConfirmationWidget
        isOpen={Boolean(detachTarget)}
        onClose={() => setDetachTarget(null)}
        onConfirm={() => {
          if (!detachTarget) return;
          const { phone, profile } = detachTarget;
          void wrap(async () => {
            const next = await cpApi.detachSmsPhoneProfile(phone.id, profile.id);
            setDetachTarget(null);
            return next;
          });
        }}
        title={t('smsPhones.detachTitle')}
        message={t('smsPhones.detachMsg')}
        itemName={detachTarget ? detachTarget.profile.name || `#${detachTarget.profile.id}` : undefined}
        confirmText={t('smsPhones.detach')}
        isConfirmLoading={busy}
      />
    </div>
  );
}
